/**
 * Invoices and bills resolve records another import writes and never create
 * them, so a document option is only ever on together with its related-record
 * prerequisites. They also depend on the general ledger: documents dated before
 * the bookkeeping start date post no entries of their own, so without the
 * pre-cutover ledger their receivables or payables would have no opening
 * entries behind them. Turning the general ledger off therefore drops both.
 * Shared so both migration entry points enforce the same rule.
 */
import { MIGRATION_OBJECT_TYPE_SYNC_FIELDS } from "@common/helpers/accountingMigrationObjectTypes";
import { MigrationObjectType } from "@common/types/domains/accounting/accountingMigration";
import { HistoricalDataValues } from "./lib";

export const MIGRATION_OBJECT_TYPE_PREREQUISITES: ReadonlyMap<
    MigrationObjectType,
    ReadonlySet<MigrationObjectType>
> = new Map([
    [
        MigrationObjectType.INVOICES,
        new Set([
            MigrationObjectType.GENERAL_LEDGER,
            MigrationObjectType.COUNTERPARTIES,
            MigrationObjectType.CATALOG_ITEMS,
        ]),
    ],
    [
        MigrationObjectType.BILLS,
        new Set([
            MigrationObjectType.GENERAL_LEDGER,
            MigrationObjectType.COUNTERPARTIES,
        ]),
    ],
]);

export type SyncToggleFields = Pick<
    HistoricalDataValues,
    | "syncGeneralLedger"
    | "syncCounterparties"
    | "syncAttachments"
    | "syncClasses"
    | "syncCatalogItems"
    | "syncInvoices"
    | "syncBills"
>;

export type SyncTogglePatch = Partial<SyncToggleFields>;

function withPrerequisitesTicked({
    objectType,
    patch,
}: {
    objectType: MigrationObjectType;
    patch: SyncTogglePatch;
}): SyncTogglePatch {
    const result: SyncTogglePatch = { ...patch };
    for (const prerequisite of MIGRATION_OBJECT_TYPE_PREREQUISITES.get(
        objectType,
    ) ?? []) {
        result[MIGRATION_OBJECT_TYPE_SYNC_FIELDS[prerequisite]] = true;
    }
    return result;
}

function withDependentsDropped({
    objectType,
    patch,
}: {
    objectType: MigrationObjectType;
    patch: SyncTogglePatch;
}): SyncTogglePatch {
    const result: SyncTogglePatch = { ...patch };
    for (const [
        dependent,
        prerequisites,
    ] of MIGRATION_OBJECT_TYPE_PREREQUISITES) {
        if (prerequisites.has(objectType)) {
            result[MIGRATION_OBJECT_TYPE_SYNC_FIELDS[dependent]] = false;
        }
    }
    return result;
}

export function getSyncGeneralLedgerTogglePatch(
    fields: SyncToggleFields,
): SyncTogglePatch {
    const syncGeneralLedger = !fields.syncGeneralLedger;
    return syncGeneralLedger
        ? { syncGeneralLedger }
        : withDependentsDropped({
              objectType: MigrationObjectType.GENERAL_LEDGER,
              patch: { syncGeneralLedger },
          });
}

export function getSyncAttachmentsTogglePatch(
    fields: SyncToggleFields,
): SyncTogglePatch {
    return { syncAttachments: !fields.syncAttachments };
}

export function getSyncClassesTogglePatch(
    fields: SyncToggleFields,
): SyncTogglePatch {
    return { syncClasses: !fields.syncClasses };
}

export function getSyncCounterpartiesTogglePatch(
    fields: SyncToggleFields,
): SyncTogglePatch {
    const syncCounterparties = !fields.syncCounterparties;
    return syncCounterparties
        ? { syncCounterparties }
        : withDependentsDropped({
              objectType: MigrationObjectType.COUNTERPARTIES,
              patch: { syncCounterparties },
          });
}

export function getSyncCatalogItemsTogglePatch(
    fields: SyncToggleFields,
): SyncTogglePatch {
    const syncCatalogItems = !fields.syncCatalogItems;
    return syncCatalogItems
        ? { syncCatalogItems }
        : withDependentsDropped({
              objectType: MigrationObjectType.CATALOG_ITEMS,
              patch: { syncCatalogItems },
          });
}

export function getSyncInvoicesTogglePatch(
    fields: SyncToggleFields,
): SyncTogglePatch {
    const syncInvoices = !fields.syncInvoices;
    return syncInvoices
        ? withPrerequisitesTicked({
              objectType: MigrationObjectType.INVOICES,
              patch: { syncInvoices },
          })
        : { syncInvoices };
}

export function getSyncBillsTogglePatch(
    fields: SyncToggleFields,
): SyncTogglePatch {
    const syncBills = !fields.syncBills;
    return syncBills
        ? withPrerequisitesTicked({
              objectType: MigrationObjectType.BILLS,
              patch: { syncBills },
          })
        : { syncBills };
}

export function applyHistoricalDataPatch({
    patch,
    setFieldValue,
}: {
    patch: SyncTogglePatch;
    setFieldValue: (field: string, value: boolean) => void;
}): void {
    for (const [field, value] of Object.entries(patch)) {
        if (value === undefined) {
            continue;
        }
        setFieldValue(`historicalData.${field}`, value);
    }
}

export function getQbdJsonHistoricalDataDefaultsPatch(
    fields: SyncToggleFields,
): SyncTogglePatch | null {
    const isStillInitialExtraObjects =
        !fields.syncCounterparties &&
        !fields.syncAttachments &&
        !fields.syncCatalogItems &&
        !fields.syncInvoices &&
        !fields.syncBills;
    if (isStillInitialExtraObjects) {
        return {
            syncAttachments: false,
            syncCatalogItems: true,
            syncInvoices: true,
            syncBills: true,
            syncCounterparties: true,
        };
    }
    const patch: SyncTogglePatch = {};
    if (
        !fields.syncCounterparties &&
        (fields.syncInvoices || fields.syncBills)
    ) {
        patch.syncCounterparties = true;
    }
    if (fields.syncAttachments) {
        patch.syncAttachments = false;
    }
    return Object.keys(patch).length > 0 ? patch : null;
}
